import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { IFilterProduct, IFilterTeam } from '@skystone/shared';
import {
  type IMetricViewTimePill,
  METRIC_VIEW_DEFAULT_PRESET,
  METRIC_VIEW_CUSTOM_PILL_ID,
  METRIC_VIEW_CUSTOM_PILL,
  METRIC_VIEW_TIME_PILLS,
  parseMetricViewPillParam,
  getPillRange,
  getPillGranularity,
  getCustomGranularity,
  isValidIsoDate,
} from '@/lib/metric-view/date-presets';
import { filterApi } from '@/services/filter-api';

export type MetricViewIssueType = 'all' | 'epic' | 'story' | 'bug' | 'task';

const VALID_ISSUE_TYPES: ReadonlySet<MetricViewIssueType> = new Set<MetricViewIssueType>([
  'all', 'epic', 'story', 'bug', 'task',
]);

export interface IMetricViewFilters {
  productId: string;
  teamId: string;
  pill: IMetricViewTimePill;
  issueType: MetricViewIssueType;
  from: string;
  to: string;
  granularity: ReturnType<typeof getPillGranularity>;
  /**
   * PRODOPSAI-621: include archived teams in aggregates and in the team
   * dropdown. Default false; URL-persisted as ?includeArchived=true.
   */
  includeArchived: boolean;
}

export interface IMetricViewScopeOptions {
  products: IFilterProduct[];
  teams: IFilterTeam[];
}

function parseIssueType(value: string | null): MetricViewIssueType {
  if (value && VALID_ISSUE_TYPES.has(value as MetricViewIssueType)) {
    return value as MetricViewIssueType;
  }
  return 'all';
}

export function useMetricViewFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [scopeOptions, setScopeOptions] = useState<IMetricViewScopeOptions>({ products: [], teams: [] });
  const [scopeLoading, setScopeLoading] = useState(true);

  const productId = searchParams.get('productId') ?? '';
  const teamId = searchParams.get('teamId') ?? '';
  const pill = parseMetricViewPillParam(searchParams.get('time'));
  const issueType = parseIssueType(searchParams.get('type'));
  // PRODOPSAI-621: only the literal 'true' enables it (matches the backend
  // zod transform). The default is never written to the URL.
  const includeArchived = searchParams.get('includeArchived') === 'true';

  // PRODOPSAI-648: an explicit custom range. Both ?from and ?to must be valid
  // ISO dates AND correctly ordered (from <= to) to enter custom mode; it then
  // overrides the time pill entirely. A reversed or impossible range (e.g. from
  // a hand-edited deep-link) is ignored so it can't drive a bad API interval —
  // the page falls back to the time pill instead.
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const isCustom = isValidIsoDate(fromParam) && isValidIsoDate(toParam) && fromParam <= toParam;

  const filters = useMemo<IMetricViewFilters>(() => {
    if (isCustom && fromParam && toParam) {
      return {
        productId,
        teamId,
        pill: METRIC_VIEW_CUSTOM_PILL,
        issueType,
        from: fromParam,
        to: toParam,
        granularity: getCustomGranularity(fromParam, toParam),
        includeArchived,
      };
    }
    const { from, to } = getPillRange(pill);
    return {
      productId,
      teamId,
      pill,
      issueType,
      from,
      to,
      granularity: getPillGranularity(pill),
      includeArchived,
    };
  }, [productId, teamId, pill, issueType, includeArchived, isCustom, fromParam, toParam]);

  const updateParams = useCallback((updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleProductChange = useCallback((value: string) => {
    updateParams({ productId: value, teamId: '' });
  }, [updateParams]);

  const handleTeamChange = useCallback((value: string) => {
    updateParams({ teamId: value });
  }, [updateParams]);

  const handlePillChange = useCallback((nextPillId: string) => {
    // PRODOPSAI-648: entering custom mode seeds the date inputs with the range
    // currently on screen so the picker doesn't start empty. Re-clicking
    // "Custom" while already custom is a no-op (don't collapse the range).
    if (nextPillId === METRIC_VIEW_CUSTOM_PILL_ID) {
      if (isCustom) return;
      const { from, to } = getPillRange(pill);
      updateParams({ from, to, time: '' });
      return;
    }
    // Picking any preset exits custom mode — clear from/to.
    const normalized = nextPillId === METRIC_VIEW_DEFAULT_PRESET ? '' : nextPillId;
    updateParams({ time: normalized, from: '', to: '' });
  }, [updateParams, isCustom, pill]);

  const handleCustomRangeChange = useCallback((from: string, to: string) => {
    updateParams({ from, to, time: '' });
  }, [updateParams]);

  const handleIssueTypeChange = useCallback((next: MetricViewIssueType) => {
    const normalized = next === 'all' ? '' : next;
    updateParams({ type: normalized });
  }, [updateParams]);

  const handleIncludeArchivedChange = useCallback((next: boolean) => {
    updateParams({ includeArchived: next ? 'true' : '' });
  }, [updateParams]);

  useEffect(() => {
    let cancelled = false;
    setScopeLoading(true);

    Promise.all([
      filterApi.getProducts(),
      // PRODOPSAI-621: when opted in, the team dropdown also lists archived
      // teams so one can be inspected directly.
      filterApi.getTeams(undefined, productId || undefined, includeArchived),
    ]).then(([productsRes, teamsRes]) => {
      if (cancelled) return;
      setScopeOptions({
        products: productsRes.products,
        teams: teamsRes.teams,
      });
      setScopeLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setScopeLoading(false);
    });

    return () => { cancelled = true; };
  }, [productId, includeArchived]);

  // PRODOPSAI-621 (Rabizard PR #737): a selected team that disappears from the
  // dropdown options — e.g. an archived team after the include-archived toggle
  // is turned OFF, or a stale deep-link without the flag — must not linger as
  // an invisible URL filter. Clear it once the fresh option list has loaded.
  useEffect(() => {
    if (scopeLoading || !teamId) return;
    if (!scopeOptions.teams.some((t) => t.id === teamId)) {
      updateParams({ teamId: '' });
    }
  }, [scopeLoading, scopeOptions.teams, teamId, updateParams]);

  return {
    filters,
    isCustom,
    scopeOptions,
    scopeLoading,
    pills: METRIC_VIEW_TIME_PILLS,
    handleProductChange,
    handleTeamChange,
    handlePillChange,
    handleCustomRangeChange,
    handleIssueTypeChange,
    handleIncludeArchivedChange,
  };
}
